import { useEffect, useState } from 'react';
import { OwnerDocumentDto } from '@inosio/payments-sdk';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Alert, Box, CircularProgress, Snackbar, Stack, TableContainer, TablePagination, Tooltip, Typography } from '@mui/material';
import RemoveOwnerIcon from '../../../icons/RemovePropertyIcon.svg';
import { fetchOwnerDocumentURL, getDownloading, selectOwnersState } from '../sagas/ownersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getFriendlyWordsFromDocumentType } from '../utils';
import DeleteOwnerDocumentModal from './DeleteOwnerDocumentModal';
import DownloadIcon from '../../../icons/DownloadIcon.svg';
import ReviewOtpIcon from '../../../icons/ReviewOtpIcon.svg';
import { MIME_TYPES_VIEWABLE_IN_BROWSER } from '../../../utils/constants';
import { getMimeTypeFromFileName } from '../../../shared/util/extractors';
import BrowserNotSupportedIcon from '@mui/icons-material/BrowserNotSupported';
import { getOwnerDocuments, selectOwnerDocuments, selectOwnerDocumentsDataPages, selectOwnerDocumentsState } from '../../../ownerui/documents/sagas/documentsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
interface OwnerDocumentsProps {
  ownerUuid: string;
  rowsPerPage: number;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  page: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPageNumber: number) => void;
}

export function OwnerDetailsDocuments(props: OwnerDocumentsProps) {
  const dispatch = useDispatch();

  //selectors
  const { isDownloading, isDownloadingFailed, isFetchingOwnerDocumentURL, isFetchingOwnerDocumentURLFailed, isFetchingOwnerDocumentURLSuccess, ownerDocumentURL } = useSelector(selectOwnersState);
  const ownerDocumentsDataBook = useSelector(selectOwnerDocumentsDataPages);
  const ownerDocuments = useSelector(selectOwnerDocuments);
  const { isFetchingOwnerDocumentsFailed, isFetchingOwnerDocuments } = useSelector(selectOwnerDocumentsState);

  //local states
  const [uuidToDelete, setUuidToDelete] = useState<string>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDocumentUuid, setSelectedDocumentUuid] = useState('');
  const [docDownloadErrorMessage, setDocDownloadErrorMessage] = useState<string | null>('');
  const [ownerDocumentsFetchError, setOwnerDocumentsFetchError] = useState<string | null>('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const tableData = ownerDocumentsDataBook?.[props.page];

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleDeleteDocumentSuccess = () => {
    dispatch({
      type: getOwnerDocuments.type,
      payload: {
        ownerUuid: props.ownerUuid,
        ownerDocumentSearchParamsDto: {
          RangeSize: props.rowsPerPage,
          StartIndex: 0,
        },
      },
    });
  };

  //error handling for document download/view
  useEffect(() => {
    if (isDownloadingFailed || isFetchingOwnerDocumentURLFailed) {
      setDocDownloadErrorMessage('Apologies, the request to download/view the document failed. Please try again later or contact support. Thank you.');
      setSnackBarOpen(true);
    }
  }, [isDownloadingFailed, isFetchingOwnerDocumentURLFailed]);

  //if you successfully get the url, open the doc in a new tab
  useEffect(() => {
    if (isFetchingOwnerDocumentURLSuccess && selectedDocumentUuid) {
      setDocDownloadErrorMessage(null);
      setSnackBarOpen(false);
      window.open(ownerDocumentURL);
    }
  }, [isFetchingOwnerDocumentURLSuccess]);

  //error handling for documents fetch of an owner
  useEffect(() => {
    if (isFetchingOwnerDocumentsFailed) {
      setOwnerDocumentsFetchError('Apologies, the request to view documents failed. Please try again later or contact support. Thank you');
    } else {
      setOwnerDocumentsFetchError(null);
    }
  }, [isFetchingOwnerDocumentsFailed]);

  return (
    <Stack sx={{ height: 'calc( 100vh - 225px )', width: '100%', overflow: 'auto' }}>
      {isFetchingOwnerDocumentsFailed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%', p: 2 }}>
          <Typography
            style={{
              fontFamily: "'Work Sans', sans-serif",
              fontStyle: 'normal',
              fontWeight: 'normal',
              fontSize: '18px',
              lineHeight: '22px',
              color: 'red',
              marginTop: '28px',
              marginBottom: '28px',
              textAlign: 'center',
            }}
          >
            {ownerDocumentsFetchError}
          </Typography>
        </Box>
      )}
      {isFetchingOwnerDocuments && (
        <Stack justifyContent="center" alignItems="center" style={{ height: '100%', width: '100%' }}>
          <SpinnerWithNoConfirmation />
        </Stack>
      )}
      <TableContainer style={{ flex: 1, marginTop: 16 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="left" alignItems="center" spacing={1}>
                  Document Description
                </Stack>
              </TableCell>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="left" alignItems="center" spacing={1}>
                  Property
                </Stack>
              </TableCell>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="left" alignItems="center" spacing={1}>
                  Type
                </Stack>
              </TableCell>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="left" alignItems="center" spacing={1}>
                  Date
                </Stack>
              </TableCell>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                  Delete
                </Stack>
              </TableCell>
              <TableCell id="subTitle" align="center">
                View
              </TableCell>
              <TableCell id="subTitle">
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                  Download
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.Rows &&
              tableData.Rows.map((eachRow: OwnerDocumentDto) => {
                return (
                  <TableRow key={eachRow.DocumentUuid}>
                    <TableCell id="content">{`${eachRow.DocumentDescription}`}</TableCell>
                    <TableCell id="content">{`${eachRow.Property}`}</TableCell>
                    <TableCell id="content">{`${getFriendlyWordsFromDocumentType(eachRow.Type)}`}</TableCell>
                    <TableCell id="content">{`${eachRow.Date}`}</TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <img
                          onClick={() => {
                            setUuidToDelete(eachRow.DocumentUuid);
                            setIsDeleteModalOpen(true);
                          }}
                          src={RemoveOwnerIcon}
                          alt="Remove document"
                          style={{ cursor: 'pointer' }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        {isFetchingOwnerDocumentURL && selectedDocumentUuid === eachRow.DocumentUuid ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={18} />
                          </Box>
                        ) : (
                          <Box>
                            {!MIME_TYPES_VIEWABLE_IN_BROWSER.includes(getMimeTypeFromFileName(eachRow.Filename) ?? '') ? (
                              <Tooltip title="This file is not viewable" placement="top">
                                <BrowserNotSupportedIcon style={{ height: 20, width: 20 }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="View Document" placement="top">
                                <img
                                  width={13}
                                  height={14}
                                  src={ReviewOtpIcon}
                                  alt="export"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setSelectedDocumentUuid(eachRow.DocumentUuid);
                                    dispatch(
                                      fetchOwnerDocumentURL({
                                        documentUuid: eachRow.DocumentUuid,
                                        fileType: '',
                                      }),
                                    );
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        {isDownloading && selectedDocumentUuid === eachRow.DocumentUuid ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={18} />
                          </Box>
                        ) : (
                          <img
                            width={13}
                            height={14}
                            src={DownloadIcon}
                            alt="Download document"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedDocumentUuid(eachRow.DocumentUuid);
                              dispatch(
                                getDownloading({
                                  documentUuid: eachRow.DocumentUuid,
                                  fileType: '',
                                }),
                              );
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 50, 100, 200]}
        count={ownerDocuments?.TotalCount || 0}
        page={props.page}
        onPageChange={props.handleChangePage}
        rowsPerPage={props.rowsPerPage}
        onRowsPerPageChange={props.handleChangeRowsPerPage}
      />
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {docDownloadErrorMessage}
        </Alert>
      </Snackbar>
      {isDeleteModalOpen && !!uuidToDelete && (
        <DeleteOwnerDocumentModal
          open={isDeleteModalOpen}
          ownerUuid={props.ownerUuid}
          uuidToDelete={uuidToDelete}
          closeCallback={() => setIsDeleteModalOpen(false)}
          onSuccessCallback={handleDeleteDocumentSuccess}
        />
      )}
    </Stack>
  );
}
